import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ButtonVariant } from "../../config/colors";
import { ButtonAlignement } from "../../components/ConfirmModal";
import { PlebData } from "../../models/Pleb";
import { LoginType } from "../../models/LoginType";

export interface ConfirmButton {
    label: string;
    variant?: ButtonVariant;
    href?: string;
    target?: string;
}

interface StoredButton extends ConfirmButton {
    actionId: string;
}

export interface OtpDialogData {
    loginType: LoginType;
    otpId: string;
    email: string;
    npub: string;
}

interface ConfirmState extends OpenConfirmPayload {
    isOpen: boolean;
    connectDialogIsOpen: boolean;
    connectDialogData: PlebData | null;
    otpDialogIsOpen: boolean;
    otpDialogData: OtpDialogData | null;
}

interface OpenConfirmPayload {
    title: string | null;
    message: string | null;
    buttonAlignement?: ButtonAlignement;
    buttons: StoredButton[];
}

const initialState: ConfirmState = {
    isOpen: false,
    title: '',
    message: '',
    buttonAlignement: ButtonAlignement.column,
    buttons: [],
    connectDialogIsOpen: false,
    connectDialogData: null,
    otpDialogIsOpen: false,
    otpDialogData: null
};

// Keep track of button handlers outside Redux
const buttonHandlers = new Map<string, () => void>();

// Function to register handlers
export const registerConfirmHandlers = (buttons: Array<{ onClick?: () => void }>) => {
    buttonHandlers.clear();
    return buttons.map((button, index) => {
        const actionId = `${Date.now()}-${index}`;
        if (button.onClick) {
            buttonHandlers.set(actionId, button.onClick);
        }
        return actionId;
    });
};

const confirmSlice = createSlice({
    name: 'confirm',
    initialState,
    reducers: {
        openConfirm: (state, action: PayloadAction<OpenConfirmPayload>) => {
            state.isOpen = true;
            state.title = action.payload.title;
            state.message = action.payload.message;
            state.buttons = action.payload.buttons;
            state.buttonAlignement = action.payload.buttonAlignement ?? ButtonAlignement.column;
        },
        closeConfirm: (state) => {
            state.isOpen = false;
            buttonHandlers.clear(); // Clean up handlers
        },
        handleButtonClick: (state, action: PayloadAction<string>) => {
            const handler = buttonHandlers.get(action.payload);
            if (handler) {
                handler();
            }
            state.isOpen = false;
            buttonHandlers.clear(); // Clean up handlers
        },
        openConnectDialog: (state, action: PayloadAction<PlebData>) => {
            state.connectDialogIsOpen = true;
            state.connectDialogData = action.payload;
        },
        closeConnectDialog: (state) => {
            state.connectDialogIsOpen = false;
            state.connectDialogData = null;
        },
        openOtpDialog: (state, action: PayloadAction<OtpDialogData>) => {
            state.otpDialogIsOpen = true;
            state.otpDialogData = action.payload;            
        },
        closeOtpDialog: (state) => {
            state.otpDialogIsOpen = false;
            state.otpDialogData = null;
        }
    }
});

// Export the action handlers map for use in components
export const getButtonHandler = (actionId: string) => buttonHandlers.get(actionId);

export const { openConfirm, closeConfirm, handleButtonClick, openConnectDialog, closeConnectDialog, openOtpDialog, closeOtpDialog } = confirmSlice.actions;
export default confirmSlice.reducer; 